<script>
import { defineAsyncComponent } from 'vue';
import { componentStore } from '@common/componentStore/componentStore';
import { configStore } from '@common/configStore';
import mockConfig from './mockConfig';
import profileCard from './components/profileCard';
import profileTabs from './components/profileTabs.vue';
import profileHeader from './components/profileHeader.vue';

let profileViewConfig = {
    cId: 'profileView',
    cType: 'profile-view',
    cTitle: 'Profile View',
    showInCmpList: false,
    cTemplate: `<div class="profile-view">
        <div v-if="loading" class="profile-view-loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
        </div>
        <template v-else>
         <div class="profile-view-sticky-header">
            <div class="profile-view-breadcrumb">
                <span class="profile-view-breadcrumb-back" @click="$emit('close')">
                    {{ pspLabel }}
                </span>
                <span> > </span>
                <span>{{ recordId }}</span>
            </div>
            <div class="profile-view-body">
                    <profile-header
                        :prop-psp="psp"
                        :prop-record-id="recordId"
                        :prop-custom-c-id="config.headerCId"
                        :prop-header-config="config.header"
                    ></profile-header>
                    <profile-tabs
                        :tabs="tabs"
                        :active-tab="activeTab"
                        @change-tab="selectTab"
                    ></profile-tabs>
                </div>
                <div v-if="activeTabObject && visitedTabs[activeTabObject.id]" class="profile-page" :key="activeTabObject.id">
<!--                    <h1>{{activeTabObject.label}}</h1>-->
                    <!-- If tab is marked as full page, render its component directly -->
                    <div v-if="activeTabObject.isFullPage" class="profile-page-full">
                        <profile-card
                            v-if="activeTabObject.cards && activeTabObject.cards.length"
                            :card="activeTabObject.cards[0]"
                            :psp="psp"
                            :record-id="recordId"
                        ></profile-card>
                    </div>

                    <!-- Otherwise, render the dashboard layout -->
                    <div v-else class="profile-page-dashboard">
                        <ejs-dashboardlayout
                            :panels="allTabPositions[activeTabObject.id]"
                            :columns="6"
                            :allowDragging="false"
                            :created="tabCreatedHandlers[activeTabObject.id]"
                        ></ejs-dashboardlayout>
                        <div v-if="tabPanelReady[activeTabObject.id]">
                            <teleport
                                v-for="(card, index) in activeTabObject.cards"
                                :key="card.id"
                                :to="allTabPositions[activeTabObject.id][index].contentId"
                            >
                                <profile-card
                                    :card="card"
                                    :psp="psp"
                                    :record-id="recordId"
                                ></profile-card>
                            </teleport>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>`
};

function extractFields(obj, fields = new Set()) {
    if (!obj || typeof obj !== 'object') return fields;
    if (Array.isArray(obj)) {
        obj.forEach((item) => extractFields(item, fields));
    } else {
        Object.entries(obj).forEach(([key, val]) => {
            if (key === 'field' && typeof val === 'string') fields.add(val);
            else extractFields(val, fields);
        });
    }
    return fields;
}

let profileViewCmp = {
    name: 'profile-view',
    components: {
        'ejs-dashboardlayout': defineAsyncComponent(() => import('@common/syncfusion/dashboardLayout')),
        'profile-card': profileCard,
        'profile-tabs': profileTabs,
        'profile-header': profileHeader
    },
    props: {
        psp: { type: String, required: true }
        // record identity via mixin context props: contextVolId, contextOppId, contextOrgId, etc.
        // access as: this.volId, this.oppId, this.orgId, this.aeId
    },
    data() {
        return {
            config: {},
            loading: true,
            activeTab: null,
            visitedTabs: {},
            tabPanelReady: {},
        };
    },
    computed: {
        recordId() {
            const map = {
                vol: this.volId,
                opp: this.oppId,
                org: this.orgId,
                ae: this.aeId,
            };
            return map[this.psp] ?? null;
        },
        pspLabel() {
            return configStore.pspParentNamesPlural[this.psp] ?? this.psp;
        },
        tabs() {
            return this.config?.tabs ?? [];
        },
        activeTabObject() {
            if (!this.activeTab) return null;
            return this.tabs.find((tab) => tab.id === this.activeTab);
        },
        allTabPositions() {
            const result = {};
            for (const tab of this.tabs) {
                result[tab.id] = tab.cards.map((card, index) => {
                    const panelId = `panel_content_${tab.id}_${index}`;
                    return {
                        id: card.id,
                        title: card.title,
                        row: card.position.row,
                        col: card.position.col,
                        sizeX: card.position.sizeX,
                        sizeY: card.position.sizeY,
                        content: `<div id="${panelId}"></div>`,
                        contentId: `#${panelId}`,
                        positionIndex: index,
                        zIndex: 1, // overrides Syncfusion's Panel default of 1000
                    };
                });
            }
            return result;
        },
        tabCreatedHandlers() {
            const handlers = {};
            for (const tab of this.tabs) {
                const tabId = tab.id;
                handlers[tabId] = () => { this.tabPanelReady[tabId] = true; };
            }
            return handlers;
        },
    },
    methods: {
        async onLoad() {
            // TODO: replace mockConfig with configStore.getConfig(this.cId) when hooked up
            this.config = mockConfig;

            const fields = [...extractFields(this.config)];
            const subscription = this.recordStore.subscribe({
                source: 'sds_getProfileField',
                params: {
                    recordId: this.recordId,
                    fields,
                    recordPsp: this.psp,
                },
            }, this.cId);

            await subscription.ready;

            this.loading = false;

            if (this.tabs.length) {
                const firstTabId = this.tabs[0].id;
                this.activeTab = firstTabId;
                this.visitedTabs[firstTabId] = true;
            }
        },
        selectTab(tabId) {
            this.activeTab = tabId;
            this.visitedTabs[tabId] = true;
        },
    },
};

componentStore.initComponent(profileViewCmp, profileViewConfig);
export default profileViewCmp;
</script>

<style scoped>
    .profile-view {
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
    .profile-view-body {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }
    .profile-view-sticky-header {
        position: sticky;
        top: 0;
        background: white;
        z-index: 2;
    }
    .profile-page {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
    }
    .profile-page-full, .profile-page-dashboard {
        flex-grow: 1;
    }
    .profile-page-dashboard :deep(.e-dashboardlayout) {
        height: max-content !important;
        width: auto !important;
    }
    .profile-page h1 {
        margin: 0 0 var(--space-4);
    }
    .profile-view-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        font-size: var(--text-size-h3);
        color: var(--primary-700);
    }
</style>
